import { PERIOD } from './chart-data';
import type { Candle } from './chart-data';
import { TIMEFRAMES } from './zone-math';
import type { Timeframe } from './zone-math';

export type Tick = { time: number; bid: number; ask: number; price?: never } | { time: number; price: number; bid?: never; ask?: never };
export const tickPrice = (tick: Tick) => tick.price ?? tick.bid;
type LiveBar = Candle & { firstTick: number; lastTick: number; complete: boolean };
export type LiveBars = { bars: Partial<Record<Timeframe, LiveBar[]>>; coverageFrom: number; coveredUntil: number };
export const emptyBars = (): LiveBars => ({ bars: {}, coverageFrom: 0, coveredUntil: 0 });

export function validTick(tick: Tick, now = Date.now()) {
  return tick && Number.isSafeInteger(tick.time) && tick.time > 0 && tick.time <= now + 10000 &&
    (tick.price !== undefined
      ? Number.isFinite(tick.price) && tick.price > 0 && tick.bid === undefined && tick.ask === undefined
      : Number.isFinite(tick.bid) && tick.bid > 0 && Number.isFinite(tick.ask) && tick.ask >= tick.bid);
}

// Preserve observed OHLC even after the server's two-minute tick window expires.
// A gap in coverage cannot create a supposedly complete candle from partial ticks.
export function accumulateTicks(previous: LiveBars, ticks: Tick[], from: number, until: number, now = Date.now(), sessionOffsetMinutes = 0): LiveBars {
  if (!Number.isInteger(sessionOffsetMinutes) || Math.abs(sessionOffsetMinutes) > 840) throw new Error('Invalid broker session');
  if (!Number.isSafeInteger(from) || !Number.isSafeInteger(until) || from < 0 || from > until || until > now + 10000 || ticks.some(tick => !validTick(tick, now))) throw new Error('Invalid live window');
  if (until < previous.coveredUntil) return previous;
  const gap = previous.coveredUntil > 0 && from > previous.coveredUntil;
  const coverageFrom = !previous.coveredUntil || gap ? from : previous.coverageFrom;
  const ordered = ticks.slice().sort((a, b) => a.time - b.time);
  const bars: LiveBars['bars'] = {};
  for (const tf of TIMEFRAMES) {
    const period = PERIOD[tf];
    const map = new Map((previous.bars[tf] || []).map(bar => [bar.time, { ...bar,
      complete: bar.complete && !(gap && bar.time + period > previous.coveredUntil && bar.time < from),
    }]));
    for (const tick of ordered) {
      const price = tickPrice(tick);
      const offset = sessionOffsetMinutes * 60000;
      const time = Math.floor((tick.time + offset) / period) * period - offset;
      const bar = map.get(time);
      if (bar) {
        bar.high = Math.max(bar.high, price); bar.low = Math.min(bar.low, price);
        if (tick.time < bar.firstTick) { bar.firstTick = tick.time; bar.open = price; }
        if (tick.time >= bar.lastTick) { bar.lastTick = tick.time; bar.close = price; }
      } else map.set(time, { time, open: price, high: price, low: price, close: price, volume: 0,
        firstTick: tick.time, lastTick: tick.time, complete: time >= coverageFrom });
    }
    bars[tf] = [...map.values()].sort((a, b) => a.time - b.time).slice(-600);
  }
  return { bars, coverageFrom, coveredUntil: until };
}

export function withLiveBars(history: Candle[], live: LiveBars, tf: Timeframe, historyAsOf = 0) {
  if (!history.length || !live.bars[tf]?.length) return history;
  const result = history.slice(), lastTime = history.at(-1)!.time;
  for (const bar of live.bars[tf]!) {
    if (bar.time < lastTime) continue;
    if (bar.time === lastTime) {
      const source = history.at(-1)!;
      result[result.length - 1] = { ...source, high: Math.max(source.high, bar.high), low: Math.min(source.low, bar.low),
        close: bar.lastTick >= historyAsOf ? bar.close : source.close };
    } else if (bar.complete) {
      const { time, open, high, low, close, volume } = bar;
      result.push({ time, open, high, low, close, volume });
    }
  }
  return result.slice(-(['H4', 'D1'].includes(tf) ? 300 : 600));
}

